import { useState } from "react";
import { FormProvider } from "react-hook-form";

import { Image } from "@/components/Atoms";
import FormInputs from "@/components/Organisms/HookFormInputs/V1";
import Typography from "@/foundation/Typography/V2";

import BookSlotFormWrapper from "../../BookSlotFormWrapper";
import BookSlotForm from "../BookSlotForm";
import useBookTrialForm from "./useBookTrialForm";

import { classNameGenerator, clsx } from "@/utils";

import { useAppSelector } from "@/hooks";

import styles from "./BookTrialForm.module.scss";

import yogaGoals from "@/redux/BootCampReducer/BootcampData/yogaGoals";

interface BookTrialFormProps {
  showModal?: boolean;
  setShowModal?: (showModal: boolean) => void;
}

const BookTrialForm: React.FC<BookTrialFormProps> = ({
  showModal,
  setShowModal = () => {},
}) => {
  const cls = classNameGenerator(styles);

  const [slot, setSlot] = useState();

  const { data } = useAppSelector((state) => state.bootCamp);
  const [formError, setFormError] = useState("");

  const { imageUrl, label, prefix, background } = data?.formSection || {};
  const { currencySymbol, price } = data?.trialData || {};

  const { methods, isLoading, onBlur, packPurchaseHandler } = useBookTrialForm({
    packData: data?.trialData,
    setFormError,
    slot,
  });

  const components = [
    {
      name: "name",
      props: {
        name: "name",
        className: cls("name"),
      },
    },
    {
      name: "email",
      props: {
        name: "email",
        className: cls("email"),
        onBlur,
      },
    },
    {
      name: "phone",
      props: {
        name: "mobile",
        className: cls("phone"),
        onBlur,
      },
    },
    {
      name: "submit",
      props: {
        children: (
          <Typography
            variant="span"
            dangerouslySetInnerHTML={{
              __html: `Confirm & Pay ${currencySymbol} ${price}`,
            }}
          />
        ),
        className: cls("submitButton"),
        isLoading,
      },
    },
    {
      props: {
        children: (
          <Typography
            variant="paragraph1"
            className={cls("infoText")}
            dangerouslySetInnerHTML={{
              __html:
                "Once enrolled, download the Shyft app to <br/>attend your class.",
            }}
          />
        ),
      },
    },
    {
      props: {
        children: (
          <Typography
            variant="paragraph1"
            className={cls("tncNote")}
            dangerouslySetInnerHTML={{
              __html: "* This purchase is non refundable",
            }}
          />
        ),
      },
    },
  ];

  return (
    <div className={cls("bookTrialForm")}>
      <div
        className={clsx(
          cls("bookTrialFormWrapper"),
          !showModal ? cls("standalone") : ""
        )}
      >
        {!slot ? (
          showModal ? (
            <BookSlotFormWrapper
              title="Book your spot!"
              subtitle="Select a time slot & submit your details <br/>to book your <strong>yoga class</strong>"
            >
              <BookSlotForm setSlot={setSlot} setShowSlotModal={setShowModal} />
            </BookSlotFormWrapper>
          ) : (
            <>
              <div style={{ background }} className={cls("imageContainer")}>
                <Image
                  src={imageUrl}
                  alt="yoga image"
                  className={cls("image")}
                />
                <div className={cls("heading")}>
                  <Typography
                    variant="paragraph1"
                    className={cls("prefix")}
                    dangerouslySetInnerHTML={{ __html: prefix }}
                  />
                 {currencySymbol &&  price !== null && <Typography
                    variant="paragraph1"
                    className={cls("label")}
                    dangerouslySetInnerHTML={{
                      __html: `${label} ${currencySymbol} ${price}`,
                    }}
                  />}
                </div>
              </div>
              <BookSlotForm setSlot={setSlot} />
            </>
          )
        ) : (
          <BookSlotFormWrapper
            title="Confirm details"
            subtitle="Submit your details to book your <br/><strong>yoga class</strong>"
          >
            <div className={cls("goBackButton")} onClick={() => setSlot(null)}>
              {backArrow}
              <Typography variant="paragraph1" className={cls("text")}>
                Go Back
              </Typography>
            </div>
            <FormProvider {...methods}>
              <form
                className={cls("formWrapper")}
                onSubmit={packPurchaseHandler}
              >
                <FormInputs components={components} />
              </form>
            </FormProvider>
          </BookSlotFormWrapper>
        )}
      </div>
    </div>
  );
};

export default BookTrialForm;

const backArrow = (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M12 14L8 10L12 6"
      stroke="#444CE7"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
